import { HttpStatusCode } from '@angular/common/http';
import { Directive, EventEmitter, Injectable, Output } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import { OrderService, PlayerService } from '.';
import { CheckoutPaymentMethodsAgreement, PaymentProviderMethod } from '../+checkout-purchase/components/checkout-payment-methods.component/interfaces';
import { PaymentRepository } from '../infrastructure/repositories/payment-repository';
import { OrderState } from '../model/enums/order-state';
import { PaymentResponseType } from '../model/enums/payment-response-type';
import { SCAPaymentEvent } from '../model/enums/sca-payment-event';
import { BankAccount } from '../model/interfaces/bank-account';
import { BillwerkAddPaymentMethodRequest } from '../model/interfaces/billwerk-add-payment-method-request';
import { BillwerkAddPaymentMethodResponse } from '../model/interfaces/billwerk-add-payment-method-response';
import { BillwerkChargeRecurringRequest, BillwerkChargeRequest } from '../model/interfaces/billwerk-charge-recurring';
import { BillwerkCustomer } from '../model/interfaces/billwerk-customer';
import { BillwerkPaymentMethod } from '../model/interfaces/billwerk-payment-method';
import { Order } from '../model/interfaces/order';
import { PayMethod } from '../model/interfaces/pay-method';
import { ReceiptService } from './receipt-service';

@Directive()
@Injectable()
export class PaymentService {
    @Output('paymentRejected')
    public paymentRejected = new EventEmitter<PaymentResponseType>();

    @Output('bsMandateCreated')
    public bsMandateCreated = new EventEmitter();

    @Output('fmtPaymentCompleted')
    public fmtPaymentCompleted = new EventEmitter();

    public onPaymentComplete = new EventEmitter<Order>();
    private bankAccount: BankAccount;
    private payMethods$?: Observable<PayMethod[]>;

    public constructor(
        private playerService: PlayerService,
        private orderService: OrderService,
        private paymentRepository: PaymentRepository,
        private receiptService: ReceiptService
    ) {}

    public executeBSAppSale(order: Order): boolean {
        if (order.NetOrderState !== OrderState.Completed) {
            return;
        }

        return this.handlePaymentResponse(PaymentResponseType.Success, order);
    }

    private handlePaymentResponse(responseType: PaymentResponseType, paidOrder: Order): boolean {
        if (responseType !== PaymentResponseType.Success) {
            this.paymentRejected.emit(responseType);
            return false;
        }

        if (paidOrder != null && paidOrder.NetOrderState === OrderState.Completed) {
            this.paymentCompleted(paidOrder);
            this.receiptService.clearOrders();
            this.receiptService.queueOrderSubscription(paidOrder);
            this.orderService.orderCompleted();
        }
        else {
            return false;
        }

        return true;
    }

    public paymentCompleted(order: Order) {
        this.onPaymentComplete.emit(order);
    }

    public getPayMethods(): Observable<PayMethod[]> {
        if (!this.payMethods$) {
            this.payMethods$ = this.paymentRepository.getPayMethods().pipe(
                catchError((error) => {
                    // Clear the cache so the next subscriber retries instead of replaying the error forever.
                    this.payMethods$ = undefined;
                    throw error;
                }),
                shareReplay({ bufferSize: 1, refCount: false })
            );
        }
        return this.payMethods$;
    }

    public setBankAccount(bankAccount: BankAccount): void {
        this.bankAccount = bankAccount;
    }

    // TODO - refactor to use await syntax with try/catch instead of then/catch
    public async executeSaleBs(): Promise<boolean> {
        const order = await this.orderService.getOrder(false);
        const player = await firstValueFrom(this.playerService.getPlayerObservable());
        const paymentResponsePromise = this.paymentRepository.executeSaleBs(
            player,
            order,
            this.bankAccount);

        let responseType: PaymentResponseType;
        let paidOrder: Order;

        return paymentResponsePromise.then((value) => {
            responseType = value.Type;
            paidOrder = value.Order;

            return this.handlePaymentResponse(responseType, paidOrder);
        }).catch((error) => {
            return this.handlePaymentResponse(PaymentResponseType.UnknownError, order);
        });
    }

    public async executeAuthenticatedBsSale(): Promise<boolean> {
        /// Make sure order lives...
        const order = await this.orderService.getOrder(false);
        const orderId = order.Id;

        const paymentResponsePromise = this.paymentRepository.executeAuthenticatedBsSale(
            orderId,
            this.bankAccount);

        let responseType: PaymentResponseType;
        let paidOrder: Order;

        return paymentResponsePromise.then((value) => {
            responseType = value.Type;
            paidOrder = value.Order;

            // notify
            this.bsMandateCreated.emit();

            return this.handlePaymentResponse(responseType, paidOrder);
        }).catch((error) => {
            return this.handlePaymentResponse(PaymentResponseType.UnknownError, order);
        });
    }

    public async executeRecurringVippsSale(vippsId: number): Promise<boolean> {
        const order = await this.orderService.getOrder(false);
        const orderId = order.Id;

        try {
            const value = await this.paymentRepository.executeRecurringVippsSale(orderId, vippsId);
            return this.handlePaymentResponse(value.Type, value.Order);
        } catch (error) {
            throw error;
        }
    }

    public async executeRecurringBsSale(bsId: number): Promise<boolean> {
        /// Make sure order lives...
        const order = await this.orderService.getOrder(false);
        const orderId = order.Id;

        const paymentResponsePromise = this.paymentRepository.executeRecurringBsSale(
            orderId,
            bsId
        );

        let responseType: PaymentResponseType;
        let paidOrder: Order;

        return paymentResponsePromise.then((value) => {
            responseType = value.Type;
            paidOrder = value.Order;

            // notify
            this.bsMandateCreated.emit();

            return this.handlePaymentResponse(responseType, paidOrder);

        }).catch((error) => {
            return this.handlePaymentResponse(PaymentResponseType.UnknownError, order);
        });
    }

    public onSCAPaymentEvent(orderId: number, event: SCAPaymentEvent): Observable<void> {
        return this.paymentRepository.sendSCAPaymentEvent(orderId, event);
    }

    //////////////////////////////////////
    ////////////// BILLWERK //////////////
    //////////////////////////////////////

    public chargePayment(payload: BillwerkChargeRequest): Observable<HttpStatusCode>
    {
        return this.paymentRepository.chargePayment(payload);
    }

    // public chargeAndSettlePayment(payload: ChargeSettle): Observable<HttpStatusCode>
    // {
    //     return this.paymentRepository.chargeAndSettlePayment(payload);
    // }

    public chargeRecurringPayment(chargeRecurringRequest: BillwerkChargeRecurringRequest): Observable<BillwerkAddPaymentMethodResponse>
    {
        return this.paymentRepository.chargeRecurringPayment(chargeRecurringRequest);
    }

    public getCustomer(playerId: number): Observable<BillwerkCustomer>
    {
        return this.paymentRepository.getCustomer(playerId);
    }

    public getCustomerPaymentMethods(playerId: number): Observable<BillwerkPaymentMethod[]>
    {
        return this.paymentRepository.getCustomerPaymentMethods(playerId);
    }

    private agreementsCache$: Observable<CheckoutPaymentMethodsAgreement[]>;

    public getActivePaymentMethodsAgreements(): Observable<CheckoutPaymentMethodsAgreement[]>
    {
        if (!this.agreementsCache$) {
            this.agreementsCache$ = this.paymentRepository.getActivePaymentMethodsAgreements().pipe(shareReplay(1));
        }
        return this.agreementsCache$;
    }

    public getActivePaymentMethods(playerId: number): Observable<BillwerkPaymentMethod[]>
    {
        return this.paymentRepository.getActivePaymentMethods(playerId);
    }

    public addPaymentMethod(paymentMethodDetails: BillwerkAddPaymentMethodRequest): Observable<BillwerkAddPaymentMethodResponse>
    {
        return this.paymentRepository.addPaymentMethod(paymentMethodDetails);
    }

    public removePaymentMethod(paymentMethodId: string): Observable<BillwerkPaymentMethod>
    {
        return this.paymentRepository.removePaymentMethod(paymentMethodId);
    }

    /**
     * Normalizes and prepares Frisbii payment method data for API requests.
     *
     * Ensures a valid payment type and ID are provided according to Frisbii’s expectations:
     * - Defaults payType to "card" if not specified or empty (Frisbii's "card" payType indicates all debit/credit card types).
     * - Returns null for paymentMethodId when no existing ID is given, signaling Frissbii to create a new payment method.
     *
     * @param paymentMethod - The raw payment method input containing optional `id` and `payType`.
     * @returns An object with the normalized `payTypeAccepted` array and resolved `paymentMethodId`.
     */
    public processPaymentProviderMethod(paymentMethod: PaymentProviderMethod) {
        // Frisbii payment method "card" accepts all available debit/credit cards
        const payTypeAccepted = [(paymentMethod.payType?.trim() || "card")];

        // If no specific id is provided, we pass null to indicate a new payment method should be created (see frisbii docs)
        const paymentMethodId = paymentMethod.id?.trim() || null;
        
        return {
            payTypeAccepted,
            paymentMethodId
        };
    }
}
